/**
 * @fileoverview Schema for project groups layout configuration.
 *
 * Defines the structure for visual project grouping on the home page.
 * Groups are purely visual - they don't affect project data, only how
 * projects are organized and displayed.
 */

import { z } from 'zod';

export const ProjectGroupSchema = z.object({
  id: z.string().describe('Unique identifier for the group'),
  name: z.string().describe('Display name of the group'),
  projectIds: z.array(z.string()).describe('Ordered list of project IDs in this group'),
  isCollapsed: z.boolean().default(false).describe('Whether the group is collapsed in the UI'),
});

export const ProjectGroupsLayoutSchema = z.object({
  userId: z.string().describe('Owner user ID'),
  updatedAt: z.string().describe('ISO timestamp of last update'),
  groups: z.array(ProjectGroupSchema).describe('All project groups'),
  layoutOrder: z.array(z.string()).describe(
    'Ordered list of top-level items. Group IDs are prefixed with "group:", project IDs are bare strings.'
  ),
});

export type ProjectGroup = z.infer<typeof ProjectGroupSchema>;
export type ProjectGroupsLayout = z.infer<typeof ProjectGroupsLayoutSchema>;

/** Prefix used to distinguish group IDs from project IDs in layoutOrder */
export const GROUP_PREFIX = 'group:';

export function isGroupLayoutItem(id: string): boolean {
  return id.startsWith(GROUP_PREFIX);
}

export function getGroupId(layoutItemId: string): string {
  return layoutItemId.replace(GROUP_PREFIX, '');
}

export function toGroupLayoutId(groupId: string): string {
  return `${GROUP_PREFIX}${groupId}`;
}
